import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hero Banner section is a wide background image with a hero banner that contains a heading, a short paragraph.
 *
 * @component
 * @param {object} props - Component properties.
 * @param {boolean} [props.isExpectingAnimations=false] - If true, applies Ken Burns animation classes to the background image.
 * @param {string} props.backgroundImageUrl - URL of the background image for the hero section.
 * @param {string} [props.i18nTitleKey] - i18n key for the hero section title. Used if `title` is not provided.
 * @param {string} [props.i18nParagraphKey] - i18n key for the hero section paragraph. Used if `paragraph` is not provided.
 * @param {string} [props.i18nCallToActionKey] - i18n key for the call-to-action button label. Used if `callToAction` is not provided.
 * @param {string} [props.title] - Direct text for the main heading. Overrides i18n key.
 * @param {string} [props.paragraph] - Direct text for the paragraph. Overrides i18n key.
 * @param {string} [props.callToAction] - Direct text for the call-to-action button. Overrides i18n key.
 * @param {function} [props.onCallToActionClick] - The function to execute when the CTA button is clicked.
 * @param {string} [props.callToActionButtonClass='uk-button-jai'] - The CSS class for the CTA button.
 * @returns {JSX.Element} The rendered HeroSection component.
 */
function HeroSection(props) {
    const {
        isExpectingAnimations = false,
        backgroundImageUrl,
        i18nTitleKey,
        i18nParagraphKey,
        i18nCallToActionKey,
        title,
        paragraph,
        callToAction,
        onCallToActionClick,
        callToActionButtonClass = 'uk-button-jai'
    } = props;

    const { t } = useTranslation();

    const animationClasses = isExpectingAnimations
        ? "uk-animation-kenburns-pingpong uk-animation-reverse uk-transform-origin-center-left"
        : "";

    return (
            <section className="uk-light uk-flex uk-flex-middle uk-overflow-hidden">
                <div style={{ height: "90vh" }}
                    className="uk-cover-container uk-width-1-1 uk-height-large">
                    <img 
                        src={backgroundImageUrl}
                        alt="Hero Section"
                        className={animationClasses}
                        uk-cover="true"
                        style={{
                            filter: "brightness(80%) blur(3px)"
                        }}
                    />
                    <div className="uk-container uk-position-cover uk-flex uk-flex-middle uk-light">
                        <div className="uk-width-2-3@s">
                            <h1 className="uk-heading uk-text-bold uk-text-glow">
                                {title || t(i18nTitleKey, { defaultValue: "Aktau — Gateway to the Caspian Sea" })}
                            </h1>
                            <p className="uk-text-lead uk-margin-small-top uk-text-glow">
                                {paragraph || t(i18nParagraphKey, { defaultValue: "Discover Aktau, a modern port city on Kazakhstan’s Caspian coast, known for its beaches, cliffs, and energy hub." })}
                            </p>
                            <button onClick={onCallToActionClick || (() => window.location.href = '/apply-visa')} className={`uk-button ${callToActionButtonClass} uk-margin-top uk-button-large`}>
                                {callToAction || t(i18nCallToActionKey, { defaultValue: "Apply for Your Kazakhstan Visa" })}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
    );
}

export default HeroSection;